import {IActionable, IContextMenuActions, IContextMenuActionsPointer, IFocusActionables} from "/@back@/commons/actionables.js";
import {BASIS} from "/@back@/commons/basis.js";
import {ActionBtn} from "/@back@/commons/widgets/buttons.js";
import {EGridDropPos, GridColDef, IGrid, IGridDataRow, IGridLineDrawer, IGridRowDropMgr} from "/@back@/commons/widgets/grid-core.js";
import {GridDataHolderJsonArray, GridDataRowJson} from "/@back@/commons/widgets/grid-libs.js";
import {Grid} from "/@back@/commons/widgets/grid-tags.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {EFolderState, GridColTreeDef, GridDataHolderJsonTree} from "/@back@/commons/widgets/tree.js";
import {CopyToSrc, CreateItem, CreateSpace, DuplicateSrc, FocusItemOrSpace, IMoveAction, ImportItemsToSrc, MoveToSrc, RenameSrc, SrcAction, WriteMainStreamToSrc} from "/@back@/wsp/actions/srcActions.js";
import {AccelKeyMgr, ACTION, Action, EButtonUiContext, IAccelKeyMgrPointer, IAction} from "/@lib@/commons/actions.js";
import {IInfo, IInfoBroker, IInfoConsumer} from "/@lib@/commons/infos.js";
import {LANG} from "/@lib@/commons/lang.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {BaseAreaViewAsync, OBaseAreaViewInit} from "/@lib@/commons/views.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IImportInterWspSvc, IImportInWspSvc, InfoCurrentItem, InfoFocusItem, InfoHighlighItemSgn, InfoReqCurrentItem, InfoSelectUris, IShortDescCtx, IShortDescsTransfer, ITEM, OImportInterWspOptions} from "/@lib@/wsp/item.js";
import {ESrcField, ESrcSt, JSrcFields, JSrcFieldsTree, SRC, srcRef, srcUri} from "/@lib@/wsp/src.js";
import {IWspUiEnv, JWspUriChangeMsg, Wsp, WSP} from "/@lib@/wsp/wsp.js";
import {EItemTypeFamily} from "/@lib@/wsp/wspMetaUi.js";
import {CellBuilderSrcIconCodeTitle, JSrcFieldsPointer, redrawSrcLine} from "/@back@/wsp/widgets/srcGridColumns.js";
import {EWspChangesEvts, WspsLivePlace} from "/@lib@/wsp/wspsLive.js";
import {TASK} from "/@lib@/wsp/lcTask.js";
import {SearchItems} from "/@lib@/wsp/search.js";
import {WspAction} from "/@back@/wsp/actions/wspActions.js";

/**
 * Widget SpaceTree : arbre des espaces et items d'un atelier avec filtre.
 *
 * TODO pour avoir d'autres colonnes de tri :
 * - désactiver les autres modes de tri lorsqu'on est en mode "search" et que les résultats sont encore incomplets,
 *   les réactiver une fois la recherche complète (ie SpaceTreeDataFiltered._afterSrcUri null).
 * - cf plus bas "FIXME Si ajout colonnes avec autres critères de tri"
 */
export interface SpaceTree extends BaseAreaViewAsync<IRegPointer<IWspUiEnv>> {
	initialize(init: OSpaceTreeInit): this
}

export interface OSpaceTreeInit extends OBaseAreaViewInit<IRegPointer<IWspUiEnv>> {

	reg?: IReg<IWspUiEnv>

	/** Racine de l'arbre. '' par défaut. */
	uriRoot?: string;

	/** Affiche la racine de l'arbre (si uriRoot renseigné). */
	showRoot?: boolean;

	/** Actions associées aux items sélectionnés (toolbar et menu contextuel). */
	actions?: IAction<IShortDescCtx>[];

	/** Racourcis claviers à capter sur la sel courante. */
	accelKeyMgr?: AccelKeyMgr<ISpaceTreeCtx>;

	/** Masque le filtre textuel. */
	hideSearch?: boolean

	/** Affiche le titre des items. */
	showTitle?: boolean

	/** Redéfinition de la colonne primaire. */
	primaryCol?: GridColTreeDef

	/** Colonnes secondaires à ajouter du grid. */
	secondaryCols?: GridColDef[]

	lineDrawers?: IGridLineDrawer[]

	/** Réagira aux infos issues de ce broker (item courant, highlight d'items selon signature) */
	itemHandlingReact?: IInfoBroker

	/** Action excutée au double-click ou sur les frappes entrée ou espace. */
	defaultAction?: IAction<ISpaceTreeCtx>

	/** Restreint l'affichage aux noeuds respectant ce filtre. */
	srcFilter?: (data: JSrcFields) => boolean;

	/** Ne permet pas l'ouverture d'un item de type dossier. */
	hideItemFolderContent?: boolean

	/** Contenu affiché si arbre vide. */
	emptyTree?: (spaceTree: SpaceTree) => Node
}

/**
 * Contexte pour les actions du SpaceTree.
 * Abstraction de SpaceTree pour être utilisé en contexte d'actions wrappées/surchagées.
 */
export interface ISpaceTreeCtx extends IShortDescCtx {
	me: SpaceTree
}

export class SpaceTree extends BaseAreaViewAsync<IRegPointer<IWspUiEnv>> implements IInfoConsumer, IGridLineDrawer, ISpaceTreeCtx, IFocusActionables<IShortDescCtx>, IContextMenuActionsPointer<IShortDescCtx>, IAccelKeyMgrPointer<IShortDescCtx> {
	reg: IReg<IWspUiEnv>;
	wsp: Wsp;
	place: WspsLivePlace;

	uriRoot: string;
	showRoot?: boolean;

	infoBroker: IInfoBroker;

	accelKeyMgr?: AccelKeyMgr<ISpaceTreeCtx>;

	/** Sélection en cours */
	shortDescs: JSrcFieldsTree[] = [];

	get emitter() {return this.grid} //on pointe le noeud focusable.
	get me() {return this} // Astuce pour retrouver le SpaceTree quand l'actionContext a été surchargé.

	/** boutons à afficher dans la focusToolbar. */
	focusActionables?: IActionable<IShortDescCtx>[];

	/** Actions associées aux items sélectionnés (mémoire pour le menuContextuel). */
	actions?: IAction<IShortDescCtx>[];

	emptyTree: (spaceTree: SpaceTree) => Node;

	grid: Grid;
	search: HTMLInputElement;
	showTitle: boolean;
	showTitleLive: boolean;

	datas: SpaceTreeDataFull;
	datasSearch: SpaceTreeDataSearchLive;

	get currentDatas(): SpaceTreeDataBase {return this.grid.dataHolder as SpaceTreeDataBase}

	/** Filtre sur l'uri, utilisé par SpaceTreeReqUri en mode search. */
	get uriFilter(): string {return this.search ? this.search.value : null}

	async selectSrcUri(srcUri: srcUri, openIt?: boolean): Promise<boolean> {
		if (this.modeSearch) {
			if (!await this.datasSearch.fetchSrcUri(srcUri)) return false;
		} else {
			await this.datas.openToTargets([srcUri]);
		}
		const rowKey = this.currentDatas.findRowKeyBySrcUri(srcUri);
		if (rowKey) {
			this.grid.setSelectedRows(this.currentDatas.getOffset(rowKey));
			if (openIt) return this.currentDatas.openFolderAsync(rowKey);
		}
		return rowKey != null;
	}

	async selectSrcUris(srcUris: srcUri[]): Promise<void> {
		if (this.modeSearch) {
			await this.datasSearch.fetchSrcUris(srcUris);
		} else {
			await this.datas.openToTargets(srcUris.concat());
		}
		this.grid.setSelectedRows(
			srcUris.map((srcUri) => this.currentDatas.findRowKeyBySrcUri(srcUri))
				.filter((rowKey) => rowKey != null)
				.map((rowKey) => this.currentDatas.getOffset(rowKey))
		);
	}

	async ensureRowVisible(srcUri: srcUri): Promise<boolean> {
		if (this.modeSearch) {
			if (!await this.datasSearch.fetchSrcUri(srcUri)) return false;
		} else {
			await this.datas.openToTargets([srcUri]);
		}
		const rowKey = this.currentDatas.findRowKeyBySrcUri(srcUri);
		if (rowKey) this.grid.ensureRowVisible(this.currentDatas.getOffset(rowKey));
		return rowKey != null;
	}

	protected async _initialize(init: OSpaceTreeInit): Promise<void> {
		super._initialize(init); //init : reg, area, areaContext
		this.wsp = this.reg.env.wsp;
		this.place = this.wsp.newPlaceWsp(); //TODO A suppr utiliser le IWspUiEnv.place ?
		this.uriRoot = init.uriRoot || "";
		this.showRoot = init.showRoot;
		this.infoBroker = init.itemHandlingReact;
		this.emptyTree = init.emptyTree;

		this._initSpaceTreeData(init);

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		if (!init.hideSearch) this._initSearchUi();
		this.showTitleLive = this.showTitle = init.showTitle;

		const colDefs: GridColDef[] = [init.primaryCol || new GridColTreeDef('srcTree')
			.setDefaultSort(1, 'ascendant')
			.setFlex('1rem', 1, 1).setMinWidth('55px').setSortable(true)
			.setRowDropMgr(rowDropMgr)
			.setCellBuilder(new CellBuilderSpaceTree(this))];
		if (init.secondaryCols) colDefs.push(...init.secondaryCols);

		this.grid = sr.appendChild(new Grid().initialize({
			selType: 'multi',
			columnDefs: colDefs,
			dataHolder: this.datas,
			hideHeaders: true,
			lineDrawer: this.infoBroker ? (init.lineDrawers ? [...init.lineDrawers, this] : this) : init.lineDrawers,
			skinOver: 'wsp-space-tree/grid',
			skinScroll: 'scroll/small',
			noResizableCol: true,
			//autoSelOnFocus: 'first', NON pb pour executer des actions (Créer un item, ...) sur la racine.
			emptyBody: (): Node => {
				if (this.datas.lastError === undefined) {
					return <c-msg label="Chargement..." level="info"/>
				} else if (this.datas.lastError === null) {
					if (this.modeSearch) return <c-msg label="Aucun item ne correspond à votre recherche" level="info"/>
					return this.emptyTree ? this.emptyTree(this) : <c-msg label="Aucun contenu" level="info"/>
				} else {
					return <c-msg label="Accès au serveur impossible" level="error"/>
				}
			},
			defaultAction: init.defaultAction,
			defaultActionCtx: this
		}));

		if (init.secondaryCols) DOM.setAttrBool(this.grid, "data-has-secondary-cols", true);

		//Impl IContextMenuActionsPointer<IShortDescCtx> sur le grid
		Object.defineProperty(this.grid, "ctxMenuActions", {
			get: (): IContextMenuActions<IShortDescCtx> | null => {return {actions: this.actions, actionContext: this, rect: this.grid.getSelRect()}}
		});

		this.grid.addEventListener('grid-select', function (this: IGrid, ev: Event) {
			const spaceTree = DOMSH.findHost<SpaceTree>(this);
			spaceTree.shortDescs = (this.dataHolder as GridDataHolderJsonArray<JSrcFieldsTree>).getSelectedDatas();
			//console.log("spaceTree.shortDescs::::", spaceTree.shortDescs);
			if (this.matches(':focus')) this.dispatchEvent(new CustomEvent<IFocusActionables<IShortDescCtx>>('c-focus-actions', {detail: spaceTree, bubbles: true, composed: true}));
		});

		this.grid.addEventListener('focus', function (this: IGrid, ev: Event) {
			this.dispatchEvent(new CustomEvent<IFocusActionables<IShortDescCtx>>('c-focus-actions', {detail: DOMSH.findHost(this) as SpaceTree, bubbles: true, composed: true}));
		});

		this.grid.linesNode.setAttribute("draggable", "true");
		this.grid.linesNode.addEventListener('dragstart', function (ev: DragEvent) {
			//console.log(ev);
			ITEM.setShortDescTransferToDragSession(DOMSH.findHost<SpaceTree>(DOMSH.findHost<IGrid>(this)), ev, 'build');
		});
		this.grid.linesNode.addEventListener('dragend', ITEM.resetShortDescTransferToDragSession);

		if (init.accelKeyMgr) {
			this.accelKeyMgr = init.accelKeyMgr;
			this.grid.addEventListener('keydown', (ev: KeyboardEvent) => {
				this.accelKeyMgr.handleKeyboardEvent(ev, this);
			});
		}

		let actions = init.actions;
		if (!actions && this.hasAttribute("actions")) actions = this.reg.mergeLists(...BASIS.extractAttr(this, 'actions').split(' '));
		if (actions) {
			actions = ACTION.injectSepByGroup(actions, this.reg.getPref("groupOrder.wsp.shortDesc", ""), this);
			this.actions = actions;
			this.focusActionables = ActionBtn.buildButtons(actions, this, 'bar');
		}
		await this.datas.initSpTrDataFull(this.place);
	}

	protected _initSearchUi() {
		this.search = <input type="search" placeholder="Filtrer..." aria-label="Filtrer" spellcheck="false"
												 onchange={this.onSearchChange} oninput={this.onSearchChange} onkeydown={this.onSearchKeydown} onfocus={this.onSearchFocus} onblur={this.onSearchBlur}/> as HTMLInputElement;
		this.shadowRoot.appendChild(<label id="search">{this.search}</label>);
	}

	/** Menu contextuel relatif à l'atelier (pas à la sel dans le grid). */
	get ctxMenuActions() {return {actions: this.actions, actionContext: Object.assign(Object.create(this), {shortDescs: []})}};

	protected _initSpaceTreeData(init: OSpaceTreeInit) {
		this.datas = new SpaceTreeDataFull(this.reg, this.uriRoot, this.showRoot, init.srcFilter);
		this.datas.callbackSearching = this;
		if (init.hideItemFolderContent) this.datas.hideItemFolderContent = true;
	}

	connectedCallback() {
		super.connectedCallback();
		if (this.infoBroker) {
			this._highlightSgn = null;
			this._assigned = null;
			const req = new InfoReqCurrentItem();
			this.infoBroker.dispatchInfo(req, this);
			this.setCurrentItem(req.srcUri || null);
			this.infoBroker.addConsumer(this);
		}
	}

	diconnectedCallback() {
		if (this.infoBroker) this.infoBroker.removeConsumer(this);
	}

	onInfo(info: IInfo): void {
		if (info instanceof InfoCurrentItem) {
			this.setCurrentItem(info.srcUri);
		} else if (info instanceof InfoHighlighItemSgn) {
			this._highlightSgn = info.sgnPattern;
			this._assigned = info.assigned;
			this.grid.invalidateRows();
		} else if (info instanceof InfoFocusItem && info.shortDesc && info.infoHolders.indexOf(this.grid) < 0) {
			//Si on a une demande extérieure de focuser un espace, on prend la main
			if (ITEM.getSrcUriType(info.shortDesc.srcUri) === "space") {
				if (this.modeSearch) this.gotoModeTree();
				this.selectSrcUri(info.shortDesc.srcUri, true).then(() => {
					this.grid.focus();
					this.ensureRowVisible(info.shortDesc.srcUri);
				});
			}
		} else if (info instanceof InfoSelectUris) {
			if (this.modeSearch) this.gotoModeTree();
			setTimeout(() => {
				//timeout car les events websockets pourraient ne pas encore être revenus.
				this.selectSrcUris(info.srcUris).then(() => {
					this.grid.focus();
					this.ensureRowVisible(info.srcUris[0]);
				});
			}, 200);
			info.handled = true;
		}
	}

	setCurrentItem(srcUri: srcUri | null) {
		this._currentSrcUri = srcUri;
		this.grid.invalidateRows();
		if (this._currentSrcUri) this.ensureRowVisible(this._currentSrcUri);
	}

	/** */
	redrawLine(row: IGridDataRow, line: HTMLElement): void {
		redrawSrcLine(this.wsp, row.rowKey, line, this._currentSrcUri, this._highlightSgn, this._assigned);
	}

	_currentSrcUri: srcUri;
	protected _highlightSgn: RegExp;
	protected _assigned: srcRef;

	/** Mode actuellement affiché. */
	get modeSearch(): boolean {return this.grid.dataHolder === this.datasSearch};

	protected onSearchChange(this: HTMLInputElement, ev: Event) {
		const me = DOMSH.findHost<SpaceTree>(this);
		if (me.modeSearch) {
			if (me.search.value.length === 0) {
				//Préservation du currentItem
				me.gotoModeTree();
				const currentItem = new InfoReqCurrentItem();
				me.infoBroker.dispatchInfo(currentItem, me);
				if (currentItem.srcUri) me.selectSrcUri(currentItem.srcUri).then(() => {
					me.ensureRowVisible(currentItem.srcUri);
				});
			} else if (me.datasSearch.request.updateParams(me)) {
				me.datasSearch.fetchSearchStart();
			}
		} else {
			if (me.search.value.length > 0) me.gotoModeSearch();
		}
	}

	onSearchKeydown(this: HTMLInputElement, ev: KeyboardEvent) {
		if (ev.key === "ArrowDown") {
			const me = DOMSH.findHost<SpaceTree>(this);
			if (me.grid.dataHolder.countRows() > 0) {
				me.grid.setSelectedRows(0);
				me.grid.focus();
				ev.stopImmediatePropagation();
				ev.preventDefault();
			}
		}
	}

	onSearchFocus(this: HTMLInputElement, ev: UIEvent) {
		const me = DOMSH.findHost<SpaceTree>(this);
		if (me.showTitleLive) {
			me.showTitleLive = false;
			me.grid.invalidateRows();
		}
	}

	onSearchBlur(this: HTMLInputElement, ev: UIEvent) {
		const me = DOMSH.findHost<SpaceTree>(this);
		if (me.showTitleLive !== me.showTitle) {
			me.showTitleLive = me.showTitle;
			me.grid.invalidateRows();
		}
	}

	protected gotoModeSearch() {
		//if(this.modeSearch) return;
		if (!this.datasSearch) this._initDataSearch();
		this.search.classList.toggle("filtered", true);
		this.grid.classList.toggle("filtered", true);
		this.grid.dataHolder = this.datasSearch;
		this.datasSearch.request.updateParams(this);
		this.datasSearch.fetchSearchStart();
	}

	protected _initDataSearch() {
		this.datasSearch = new SpaceTreeDataSearchLive(this.reg, this.uriRoot, this.showRoot, this.datas.fieldsFilter);
		this.datasSearch.callbackSearching = this;
		this.datasSearch.hideItemFolderContent = this.datas.hideItemFolderContent;
		this.datasSearch.initSpTrDataLive(this.place);
	}

	protected gotoModeTree() {
		//if(!this.modeSearch) return;
		this.search.classList.toggle("filtered", false);
		this.grid.classList.toggle("filtered", false);
		this.search.value = "";
		this.grid.dataHolder = this.datas;
	}

	onViewHidden(closed: boolean) {
		if (closed) this.place.closePlace();
	}

	protected _searchingMsg: HTMLElement;

	/** cf SpaceTreeData.callbackSearching. */
	onSearching(state: 'searchInit' | 'searchNext' | 'searchInside' | 'searchEnd') {
		//window.requestIdleCallback(() => { NON ordre d'appel searchInit -> searchEnd pas toujours garanti (Chrome/69 avec debugger).
		if (this._searchingMsg) {
			this._searchingMsg.remove();
			this._searchingMsg = null;
		}
		if (state === 'searchInit') {
			this._searchingMsg = this.shadowRoot.insertBefore(<div id="searchInit">Recherche...</div>, this.grid);
		} else if (state === 'searchNext') {
			this._searchingMsg = this.shadowRoot.insertBefore(<div id="searchNext">Recherche suivants...</div>, this.grid.nextSibling);
		}
	}

	/** Retrouve le 1er noeud de type dossier à partir d'un noeud.*/
	extractFolderUri(src: JSrcFields): srcUri {
		if (!src || !src.srcUri || !src.srcSt) return this.uriRoot;
		if (this.datas.hideItemFolderContent) {
			//Tous les items sont terminaux, on retourne toujours son espace
			return ITEM.extractSpaceUri(src.srcUri);
		} else if (!src.itModel || this.wsp.wspMetaUi.getItemType(src.itModel).isFamily(EItemTypeFamily.folder, EItemTypeFamily.undef, EItemTypeFamily.other)) {
			return src.srcSt === ESrcSt.file ? SRC.extractUriParent(src.srcUri) : src.srcUri;
		}
		return ITEM.extractSpaceUri(src.srcUri);
	}

	/** Retrouve les props du 1er noeud de type dossier à partir d'un noeud (si disponible)*/
	extractFolderSrcFields(src: JSrcFields): JSrcFields {
		const srcUri = this.extractFolderUri(src);
		if (srcUri)
			return this.datas.findRowKeyBySrcUri(srcUri);
	}

}

REG.reg.registerSkin('wsp-space-tree', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		position: relative;
	}

	#search {
		background: .1em / 1em no-repeat url(/@skin@/commons/icons/filter.svg) var(--form-search-bgcolor);
		padding-inline-start: 1.2em;
		border-bottom: 1px solid var(--border-color);
	}

	input {
		padding: 2px;
		background: var(--form-search-bgcolor);
		color: var(--form-color);
		border: none;
		width: 100%;
		font-size: inherit;
	}

	.filtered {
		background: url(/@skin@/commons/icons/filtered.svg) var(--row-bgcolor);
	}

	input:focus {
		outline: var(--focus-outline);
	}

	input::placeholder {
		color: var(--fade-color);
		letter-spacing: 2px;
		font-size: .8em;
		font-style: italic;
	}

	input:focus::placeholder {
		color: transparent;
	}

	c-grid {
		flex: 1;
		border: none;
		background-color: var(--row-bgcolor);
	}

	#searchInit, #searchNext {
		position: absolute;
		z-index: 1;
		background-color: var(--bgcolor);
		color: var(--color);
		align-self: center;
		padding: .5em;
		border: 1px solid var(--border-color);
		border-radius: 1em;
	}

	#searchInit {
		top: 2em;
	}

	#searchNext {
		bottom: 1em;
	}
`);

REG.reg.registerSkin('wsp-space-tree/grid', 1, /* language=CSS */ `
	.highlight {
		background-color: var(--row-highlight-bgcolor);
	}

	.assigned {
		background-color: var(--row-assigned-bgcolor);
	}

	/** Réservation de la largeur de background-image pour qu'il n'y ait pas de superposition avec les secondary-cols */
	:host([data-has-secondary-cols]) .line,
	:host([data-has-secondary-cols]) #resizers {
		padding-inline-end: .8rem;
	}

	.current {
		background-position: right;
		background-repeat: no-repeat;
		background-image: var(--row-current-img-end);
		background-color: var(--row-current-bgcolor);
	}
`);

customElements.define('wsp-space-tree', SpaceTree);

/**
 * Gestionnaire des données du grid (arbre json des noeuds du wsp).
 */
export abstract class SpaceTreeDataBase extends GridDataHolderJsonTree<JSrcFieldsTree> {

	wsp: Wsp;

	/** Fields de la racine de l'arbre (root de l'atelier). Renseigné uniquement si showRoot = false. */
	rootSrcFields: JSrcFields;

	hideItemFolderContent: boolean;

	/** Force l'affichage des entrées supprimées pour les ateliers draft. */
	showDeletedDrfEntries: boolean;

	/** Callback pour retour graphique d'une recherche en cours. */
	callbackSearching: { onSearching: (state: 'searchInit' | 'searchNext' | 'searchInside' | 'searchEnd') => void };

	lastError: undefined /* initing*/ | null /* pas d'erreur */ | any;

	/** Séquentilisation des requetes pour éviter des races conditions. */
	sequentialReq: Promise<any>;

	/**
	 * @param excludeTildeRootUri
	 * 	si [] (valeur par défaut), toutes les uri "/~" sont exclues, y compris airItems et extItems.
	 *  si null aucune exclusion n'est ajoutée par rapport à fieldsFilter.
	 *  si ["/~air/", "/~ext/"] les uris "/~" sont exclues sauf les airItems et extItems.
	 */
	constructor(public readonly reg: IReg<IWspUiEnv>, public uriRoot = '', public showRoot: boolean = false, public fieldsFilter?: (data: JSrcFields) => boolean, excludeTildeRootUriExcept: string[] | null = []) {
		super('ch');
		this.wsp = reg.env.wsp;
		if (excludeTildeRootUriExcept) {
			const filter = excludeTildeRootUriExcept.length === 0
				? (data: JSrcFields) => !data.srcUri.startsWith("/~")
				: (data: JSrcFields) => !data.srcUri.startsWith("/~") || excludeTildeRootUriExcept.findIndex((u) => data.srcUri.startsWith(u)) >= 0
			if (this.fieldsFilter) {
				const subFilter = this.fieldsFilter;
				this.fieldsFilter = (data: JSrcFields) => filter(data) && subFilter(data);
			} else {
				this.fieldsFilter = filter;
			}
		}
		this.setNaturalOrder((a: JSrcFieldsTree, b: JSrcFieldsTree) => this.wsp.srcUriItemsSortFn(a.srcUri, b.srcUri));
	}

	findRowKeyBySrcUri(srcUri: srcUri, lastAncestor?: boolean): JSrcFieldsTree {
		if (this.showRoot ? !SRC.isSubUriOrEqual(this.uriRoot, srcUri) : !SRC.isSubUri(this.uriRoot, srcUri)) return null;
		if (this.showRoot && srcUri === SRC.URI_ROOT) return this._datas[0];
		let offset = this.uriRoot.length + 1;
		let nextEnd = srcUri.indexOf('/', offset);
		let children = this.showRoot ? this._datas[0].ch : this._datas;
		if (!children) return;
		let previous: JSrcFieldsTree = null;
		while (nextEnd > 0) {
			//On n'est pas sur le dernier fragment
			const uriParent = srcUri.substring(0, nextEnd);
			const parent = children.find((fields: JSrcFieldsTree) => fields.srcUri === uriParent) as any;
			if (!parent) return lastAncestor ? previous : null;
			previous = parent;
			children = this.ch(parent);
			if (!children) return lastAncestor ? parent : null;
			offset = nextEnd + 1;
			nextEnd = srcUri.indexOf('/', offset);
		}
		//Dernier fragment de l'uri
		return children.find((fields: JSrcFieldsTree) => fields.srcUri === srcUri) as JSrcFieldsTree || (lastAncestor ? previous : null);
	}

	async openToTargets(srcUris: srcUri[]): Promise<void> {
		while (this.sequentialReq) await this.sequentialReq;
		if (DEBUG) console.log("openToTargetsSeq:::start", srcUris);
		const roots = this.findRootsToFetchOrOpenAnc(srcUris);
		try {
			if (roots.length === 0) return;
			if (roots.length === 1) return await (this.sequentialReq = this._openToTargets(roots[0].root, roots[0].targets));
			await (this.sequentialReq = Promise.all(roots.map((root) => this._openToTargets(root.root, root.targets))));
		} finally {
			this.sequentialReq = null;
			if (DEBUG) console.log("openToTargetsSeq:::end", roots);
		}
	}

	/** Construction par défaut du contenu. */
	protected async fetchRootDatas() {
		while (this.sequentialReq) await this.sequentialReq;
		try {
			await (this.sequentialReq = this._fetchRootDatas());
		} finally {
			this.sequentialReq = null;
		}
	}

	/** RE-Construction du contenu en tentant d'afficher targets. */
	protected async refetchTree(targets: srcUri[]) {
		while (this.sequentialReq) await this.sequentialReq;
		try {
			await (this.sequentialReq = this._refetchTree(targets));
		} finally {
			this.sequentialReq = null;
		}
	}


	/** Ne pas utiliser directement, cf fetchRootDatas(); */
	protected async _fetchRootDatas() {
		if (DEBUG) console.log("fetchRootDatas:::start");
		if (this.callbackSearching) this.callbackSearching.onSearching('searchInit');
		this.lastError = undefined;
		try {
			const datas = await WSP.fetchShortDescTree(this.wsp, this._grid, this.uriRoot, 1);
			if (!datas.ch) datas.ch = [];
			this.lastError = null;
			if (this.fieldsFilter) datas.ch = datas.ch.filter(this.fieldsFilter);
			if (this.showRoot) {
				this.rootSrcFields = null;
				this.setDatas([datas]);
				this.openFolder(datas);
			} else {
				this.rootSrcFields = datas;
				this.setDatas(datas.ch);
			}
		} catch (e) {
			this.lastError = e;
			this.setDatas([]);
			throw e;
		} finally {
			if (this.callbackSearching) this.callbackSearching.onSearching('searchEnd');
			if (DEBUG) console.log("fetchRootDatas:::end");
		}
	}

	/** Ne pas utiliser directement, cf refetchTree(); */
	protected async _refetchTree(targets: srcUri[]): Promise<void> {
		if (DEBUG) console.log("refetchTree:::start", targets);
		if (this.callbackSearching) this.callbackSearching.onSearching('searchInit');
		this.lastError = undefined;
		try {
			let scrollTopUri: srcUri;
			let scrollTopOffset: number;
			if (this._grid) {
				scrollTopOffset = this._grid.getVisibleOffsetStart();
				scrollTopUri = scrollTopOffset > 0 ? (this.getRowKey(scrollTopOffset) as JSrcFieldsTree).srcUri : null;
			}
			const datas = await WSP.fetchShortDescSubTree(this.wsp, this._grid, this.uriRoot, targets);
			this.lastError = null;
			if (!datas.ch) datas.ch = [];
			if (this.fieldsFilter) {
				const filter = (datas: JSrcFieldsTree) => {
					if (datas.ch) (datas.ch = datas.ch.filter(this.fieldsFilter)).forEach(filter);
				};
				filter(datas);
			}
			//console.log("refetchTree.rootDatas::::", datas);
			const memOpenStates = this.defaultOpenState;
			try {
				this.defaultOpenState = (data: JSrcFieldsTree) => data.ch != null;
				if (this.showRoot) {
					this.rootSrcFields = null;
					this.setDatas([datas]);
					this.openFolder(datas);
				} else {
					this.rootSrcFields = datas;
					this.setDatas(datas.ch || []);
				}
				if (this._grid) {
					const newRowKey = scrollTopUri ? this.findRowKeyBySrcUri(scrollTopUri) : null;
					if (newRowKey) {
						//srcUri retrouvé, on scroll
						this._grid.ensureRowVisible(this.getOffset(newRowKey), {block: "start", behavior: "auto"});
					} else {
						//srcUri pas retrouvé on repossitionne dans l'absolu
						this._grid.ensureRowVisible(scrollTopOffset, {block: "start", behavior: "auto"});
					}
				}
			} finally {
				this.defaultOpenState = memOpenStates;
			}
		} catch (e) {
			this.lastError = e;
			this.setDatas([]);
			throw e;
		} finally {
			if (this.callbackSearching) this.callbackSearching.onSearching('searchEnd');
			if (DEBUG) console.log("refetchTree:::end", targets);
		}
	}

	async openFolderAsync(rowKey: JSrcFieldsTree): Promise<boolean> {
		try {
			if (DEBUG) console.log("openFolderAsync:::start", rowKey.srcUri);
			if (this.folderSt(rowKey) !== EFolderState.closed) return false;
			this.setFolderSt(rowKey, EFolderState.opening);
			const datas = await WSP.fetchShortDescTree(this.wsp, this._grid, rowKey.srcUri, 1);
			if (!this.isRowkeyAlive(rowKey)) return false; //modification de l'arbre entre temps, noeud perdu.
			if (this.fieldsFilter && datas.ch) datas.ch = datas.ch.filter(this.fieldsFilter);
			if (!datas.ch) datas.ch = [];//reeval si datas est toujours un dossier ?
			this.setChilrenTo(rowKey, datas.ch, this.defaultOpenState);
			this.setFolderSt(rowKey, EFolderState.closed);
			return this.openFolder(rowKey);
		} catch (e) {
			POPUP.showNotifError("Accès au serveur impossible", this.grid);
			this.closeFolder(rowKey);
			throw e;
		} finally {
			if (DEBUG) console.log("openFolderAsync:::end", rowKey.srcUri);
		}
	}

	closeFolder(rowKey: any): boolean {
		if (super.closeFolder(rowKey)) {
			//reset des fils à la fermeture, forcera un refresh à la réouverture.
			const chList = this.ch(rowKey);
			if (chList) {
				for (const ch of chList) this._killRowData(ch);
				this.setCh(rowKey, null);
			}
			return true;
		}
		return false;
	}

	/** Ne pas utiliser directement, cf openToTargets(); */
	protected async _openToTargets(rowKey: JSrcFieldsTree, targets: srcUri[]): Promise<void> {
		try {
			const datas = await WSP.fetchShortDescSubTree(this.wsp, this._grid, rowKey.srcUri, targets);
			if (!this.isRowkeyAlive(rowKey)) return; //modification de l'arbre entre temps, noeud perdu.
			if (this.fieldsFilter) {
				const filter = (datas: JSrcFieldsTree) => {
					if (datas.ch) (datas.ch = datas.ch.filter(this.fieldsFilter)).forEach(filter);
				};
				filter(datas);
			}
			if (!datas.ch) datas.ch = [];//reeval si datas est toujours un dossier ?
			this.setChilrenTo(rowKey, datas.ch, (data: JSrcFieldsTree) => data.ch != null);
			this.openFolder(rowKey);
		} catch (e) {
			POPUP.showNotifError("Accès au serveur impossible", this.grid);
			this.closeFolder(rowKey);
			throw e;
		}
	}

	/** Attention targets est modifié par l'algo (=> cloner en amont si besoin) */
	protected findRootsToFetchOrOpenAnc(targets: srcUri[]): { root: JSrcFieldsTree, targets: srcUri[] }[] {
		const r = [] as { root: JSrcFieldsTree, targets: srcUri[] }[];
		for (let i = 0; i < targets.length; i++) {
			const target = targets[i];
			if (!target) continue;
			const rowKey = this.findRowKeyBySrcUri(target, true) || this.rootSrcFields;
			if (rowKey) {
				if (rowKey.srcUri !== target) {
					//trouvé un root à charger
					const newRoot = {root: rowKey, targets: [target]};
					r.push(newRoot);
					for (let k = i + 1; k < targets.length; k++) {
						//On ajoute les autres subUri qui ont ce root
						if (SRC.isSubUri(rowKey.srcUri, targets[k])) {
							newRoot.targets.push(targets[k]);
							targets[k] = null; //traité
						}
					}
				} else {
					//rowKey trouvé, on s'assure que ses ancêtres sont ouverts.
					const parent = this.getRowKeyParent(rowKey);
					if (parent) this.openFolder(parent);
				}
			}
		}
		return r;
	}

	protected async refreshRowKey(rowKey: JSrcFieldsTree) {
		if (this.sequentialReq) {
			while (this.sequentialReq) await this.sequentialReq;
			//Réévalue si l'insertion n'a pas déjà été réalisée
			rowKey = this.findRowKeyBySrcUri(rowKey.srcUri);
			if (!rowKey) return; //row absente, on abandonne.
		}
		try {
			await (this.sequentialReq = this._refreshRowKey(rowKey));
		} finally {
			this.sequentialReq = null;
		}
	}

	protected async _refreshRowKey(rowKey: JSrcFieldsTree) {
		const fields = await this.wsp.fetchShortDesc(rowKey.srcId || rowKey.srcUri, this._grid);
		if (!this.isRowkeyAlive(rowKey)) return; //modification de l'arbre entre temps, noeud perdu.
		if (this.mustHideNode(fields) || fields.srcUri !== rowKey.srcUri) { //cas du srcUri != : dans DRF sur rename d'une res : issu d'un msg wspChanges de type='r' dont on demande le refresh car on est en DRF, les suppr restent visibles.
			this.deleteRowKey(rowKey);
		} else {
			// FIXME Si ajout colonnes avec autres critères de tri que le srcUri => on doit repositionner au bon index dans l'arbre.
			const newRowKey = Object.assign(Object.create(null), fields);
			this.replaceRowKey(rowKey, newRowKey, true);
			if (this._grid && this._grid.isRowSelected(this.getOffset(newRowKey))) {
				//on simule une resélection pour update de SpaceTree.shortDescs.
				this._grid.dispatchEvent(new CustomEvent('grid-select', {bubbles: true, composed: true}));
			}
		}
	}

	protected async insertSrcUri(parentRowKey: JSrcFieldsTree | null, srcUri: srcUri, shortDesc?: JSrcFields) {
		if (this.sequentialReq) {
			while (this.sequentialReq) await this.sequentialReq;
			//Réévalue si l'insertion n'a pas déjà été réalisée
			if (this.findInChildren(parentRowKey ? parentRowKey.ch : this._datas, srcUri) >= 0) return; //uri insérée entre temps.
		}
		try {
			await (this.sequentialReq = this._insertSrcUri(parentRowKey, srcUri, shortDesc));
		} finally {
			this.sequentialReq = null;
		}
	}

	/** Ne pas utiliser directement, cf insertSrcUri(); */
	protected async _insertSrcUri(parentRowKey: JSrcFieldsTree | null, srcUri: srcUri, shortDesc?: JSrcFields) {
		const fields = shortDesc || await this.wsp.fetchShortDesc(srcUri, this._grid);
		if (this.mustHideNode(fields)) return;
		if (parentRowKey && !this.isRowkeyAlive(parentRowKey) && this.getRowKeyFolderState(parentRowKey) !== EFolderState.opened) return; //modification de l'arbre entre temps, noeud perdu.
		if (this.findInChildren(parentRowKey ? parentRowKey.ch : this._datas, srcUri) >= 0) return; //uri insérée entre temps.
		this.insertRowKey(parentRowKey, Object.assign(Object.create(null), fields));
	}

	protected findInChildren(children: JSrcFieldsTree[], srcUri: srcUri): number {
		return children.findIndex((e) => e.srcUri === srcUri);
	}


	/** Evalue si ce noeud doit être supprimé de l'arbre. */
	mustHideNode(fields: JSrcFields): boolean {
		if (!fields) return true;
		if (this.fieldsFilter && !this.fieldsFilter(fields)) return true;
		if (this.showDeletedDrfEntries) {
			//Mode "src supprimées visibles" (ateliers drafts)
			if (fields.srcSt === ESrcSt.none) {
				if (fields.drfState === "createdDeleted") return true; //Si atelier draft en corbeille local, on masque
				if (fields.drfState === "notOverriden") return true; //Suite à validation d'une suppr, on arrive dans ce statut notOverriden
			}
		} else {
			//Mode normal
			if (fields.srcSt === ESrcSt.none) return true; //item supprimés éliminés de l'arbre
		}
		return false
	}

	/** Préévalue à partir d'un msg JWspUriChangeMsg si l'entrée devrait être supprimée. */
	protected shouldHideEntry(msg: JWspUriChangeMsg, nodeFound?: JSrcFieldsTree): boolean {
		if (this.showDeletedDrfEntries) {
			if (nodeFound && msg.type === EWspChangesEvts.r) {
				if (nodeFound.srcSt < 0) return true; //noeud déjà supprimé, => suppr définitivie ou déplacement (retsoreTo) => on suppr.
				if (nodeFound.drfState === "created") return true; //noeud draft local => on suppr.
			}
			return false;
		}
		return msg.type === EWspChangesEvts.r;
	}

	protected _initRowData(data: JSrcFieldsTree, parent: Dict<any>, openIt: (data: any) => boolean): number {
		//On init la prop 'ch'.
		if (data.srcSt === ESrcSt.folder) {
			if (data.itModel != null) {
				//item ou res d'item.
				if (!this.hideItemFolderContent && (this.wsp.wspMetaUi.getItemType(data.itModel).isFamily(EItemTypeFamily.folder, EItemTypeFamily.undef, EItemTypeFamily.other))) {
					if (data.ch === undefined) data.ch = null; //On déclare l'état folder.
				} else if ('ch' in data) {
					//On ne veut pas voir les fils, on les élimine.
					data.ch = undefined;
				}
			} else if (!('ch' in data)) {
				data.ch = null; //On déclare l'état folder.
			}
		}
		return super._initRowData(data, parent, openIt);
	}
}

/**
 *
 */
export class SpaceTreeDataFull extends SpaceTreeDataBase {

	initSpTrDataFull(place?: WspsLivePlace): Promise<void> {
		if (place) place.eventsMgr.on('wspUriChange', (msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') => {
			if (this.wsp.code !== msg.wspCd) return;
			const srcUri = !this.hideItemFolderContent && msg.shortDesc ? msg.shortDesc.srcUri : msg.srcUri; //msg.shortDesc.srcUri => resUri dans item folder.
			if (ITEM.isSpecialUri(srcUri)) return;
			if (srcUri === this.uriRoot) {
				return this.fetchRootDatas();
			} else if (this.uriRoot && !SRC.isSubUri(this.uriRoot, srcUri)) return;
			const node = this.findRowKeyBySrcUri(srcUri);
			if (!node) {
				//source actuellement non affichée
				const parentUri = SRC.extractUriParent(srcUri);
				if (parentUri === this.uriRoot && !this.showRoot) {
					//On insert d'un nouvel objet à la racine
					if (!this.shouldHideEntry(msg)) return this.insertSrcUri(null, srcUri, msg.shortDesc);
					return;
				}
				const anc = this.findRowKeyBySrcUri(parentUri, true);
				if (!anc) {
					//Aucun ancêtre commun trouvé, on vérifie qu'on n'es pas en création d'un descendant d'une nouvelle racine
					if (!this.shouldHideEntry(msg)) {
						const chUri = srcUri.substring(0, srcUri.indexOf('/', 1));
						if (!this._datas.find((src) => src.srcUri === chUri)) {
							//nouvelle racine détectée
							return this.insertSrcUri(null, chUri);
						}
					}
				} else if (anc.srcUri === parentUri) {
					//On a trouvé le parent direct
					if (this.isRowKeyShown(anc) && this.getRowKeyFolderState(anc) === EFolderState.opened) {
						//On insert ce nouvel élément dans son dossier père visible et ouvert
						if (!this.shouldHideEntry(msg)) return this.insertSrcUri(anc, srcUri, msg.shortDesc);
					} else {
						//Le dossier parent ou un de ses ancêtre est clos.
						//On cleanup le contenu du dossier qui devra être rechargé.
						this.resetAsyncChildren(anc);
					}
				} else if (msg.type === EWspChangesEvts.u && anc.ch) {
					//dossier ancêtre trouvé, avec des fils, et msg est un update/create: ce pourrait être une création dans une sous-struct
					//entrainant la création dynammique des dossiers parents.
					const chUri = srcUri.substring(0, srcUri.indexOf('/', anc.srcUri.length + 1));
					if (!anc.ch.find((src) => src.srcUri === chUri)) {
						//Pas trouvé chUri dans anc
						if (this.isRowKeyShown(anc) && this.getRowKeyFolderState(anc) === EFolderState.opened) {
							//anc est visible et ouvert, on l'insert
							return this.insertSrcUri(anc, chUri);
						} else {
							//On cleanup le contenu du dossier qui devra être rechargé.
							this.resetAsyncChildren(anc);
						}
					}
				}
				return;
			}
			//node trouvé
			if (this.shouldHideEntry(msg, node)) {
				this.deleteRowKey(node);
			} else if (msg.type === EWspChangesEvts.s) {
				node['itSt'] = msg.itSt;
				(node as any)['icon'] = undefined; //FIXME API reset IGridDataRow.cacheHolder
				if (this._grid) {
					const offset = this.getOffset(node);
					if (offset >= 0) this._grid.invalidateRows(offset, 1);
				}
			} else {
				if (msg.type === EWspChangesEvts.drfState) {
					//Gestion des espaces ancêtres qui pourraient avoir le statut created ou overriden à supprimer.
					let parent = this.getRowKeyParent(node);
					while (parent && (parent.drfState === "created" || parent.drfState === "overriden")) {
						this.refreshRowKey(parent);
						parent = this.getRowKeyParent(parent);
					}
				}
				if (this.isRowKeyShown(node)) {
					//Ce noeud est visible.
					if (msg.type === EWspChangesEvts.u && this.getRowKeyFolderState(node) === EFolderState.opened) {
						//msg.type===u signifie en général un changement du contenu du dossier issue d'une fusion de 2 dossiers.
						//Si le dossier est ouvert, il faudrait donc un algo qui retrouve les fils ajoutés par la fusion
						//pour les injecter dans l'arbre sans perturber l'état ouvert/fermé des autres fils préexistant.
						//En attendant : on clos le dossier et on reset son contenu !
						this.closeFolder(node);
						this.resetAsyncChildren(node);
					}
					return this.refreshRowKey(node);
				} else {
					//On cleanup le contenu du dossier parent qui devra être rechargé.
					this.resetAsyncChildren(this.getRowKeyParent(node));
				}
			}
		});

		if (place) place.eventsMgr.on("onConnectionRenewed", () => {
			const targets = [] as srcUri[];

			function listTargets(datas: JSrcFieldsTree[]): boolean {
				//XXX algo moins couteux, plus complexe : on ne garde ouvert que les dossiers visibles dans le grid.
				const oldLen = targets.length;
				for (const node of datas) {
					if (node.ch) {
						//c'est un dossier ouvert
						if (listTargets(node.ch)) {
							//Ce dossier ne conteint aucun dossier fils ouvert, on ajoute ce dossier en cible
							targets.push(node.srcUri + '/');
						}
					}
				}
				return oldLen === targets.length;//true = aucun dossier fils ouvert trouvé.
			}

			if (listTargets(this._datas)) targets.push(this.uriRoot + '/');
			this.refetchTree(targets);
		});
		this.showDeletedDrfEntries = this.wsp.wspDefProps?.drfRefWsp != null;
		return this.fetchRootDatas();
	}
}

/** On hérite de SpaceTreeDataBase pour bénéficier de l'ouverture asynchrone des items de type folder. */
export class SpaceTreeDataSearch extends SpaceTreeDataBase {

	/** Chargement progressif. Donc impl Grid obligatoire (ie pas SmallGrid). */
	_grid: Grid;

	request: ISpaceTreeReq;
	maxStartSearch: number;
	maxNextSearch: number;

	/** Requete en cours (pour gérer l'attente avant insertion dans l'arbre d'une détection d'un update d'un item)*/
	protected _nextSearch: Promise<any>;

	/** Dernier noeud remonté permettant de fixer le point de départ de la prochaine recherche. */
	protected _afterSrcUri: srcUri;
	protected _afterEndFolder = false;

	connectToGrid(grid: IGrid | null) {
		super.connectToGrid(grid);
		this.listenGridLimits();
	}

	initSpTrDataSearch(req: ISpaceTreeReq, maxStartSearch: number, maxNextSearch: number) {
		this.request = req;
		this.maxStartSearch = maxStartSearch;
		this.maxNextSearch = maxNextSearch;
		this.defaultOpenState = GridDataHolderJsonTree.defaultOpened;
	}

	fetchSearchStart(resetUi?: boolean): Promise<void> {
		if (resetUi) {
			this.lastError = undefined;
			this.setDatas([]);
		}
		if (this._nextSearch) {
			const stamp = this.request.getStamp();
			this._nextSearch = this._nextSearch.then(() => {if (this.request.isSameStamp(stamp)) return this.fetchSearchStart()});
		} else {
			this._nextSearch = this._fetchSearchStart();
		}
		return this._nextSearch;
	}

	hasNextToPopulate(): boolean {return this._afterSrcUri != null}

	fetchSearchNext(count?: number): Promise<void> {
		if (!this._afterSrcUri || !this._grid) return;
		if (this._nextSearch) {
			const stamp = this.request.getStamp();
			this._nextSearch = this._nextSearch.then(() => {if (this.request.isSameStamp(stamp)) return this.fetchSearchNext(count)});
		} else {
			this._nextSearch = this._fetchSearchNext(count || this.maxNextSearch);
		}
		return this._nextSearch;
	}

	async fetchSearchReplaceChildren(folderUri: srcUri): Promise<boolean> {
		if (!this._grid) return;
		if (this._nextSearch) {
			const stamp = this.request.getStamp();
			this._nextSearch = this._nextSearch.then(() => {return this.request.isSameStamp(stamp) ? this.fetchSearchReplaceChildren(folderUri) : false});
		} else {
			this._nextSearch = this._fetchSearchReplaceChildren(folderUri, this.maxNextSearch);
		}
		return this._nextSearch;
	}

	/**
	 * Charge la liste jusqu'à l'uri soit présente dans la liste.
	 * Retourne false si srcUri ne matche pas les critères de sélection.
	 */
	async fetchSrcUri(srcUri: srcUri): Promise<boolean> {
		let guard = 0;
		while (this._afterSrcUri && this.wsp.srcUriItemsSortFn(srcUri, this._afterSrcUri) > 0 && guard < 100) {
			await this.fetchSearchNext(2000);
			guard++;
		}
		return true;
	}

	async fetchSrcUris(srcUris: srcUri[]): Promise<void> {
		let guard = 0;
		for (const srcUri of srcUris) {
			while (this._afterSrcUri && this.wsp.srcUriItemsSortFn(srcUri, this._afterSrcUri) > 0 && guard < 100) {
				await this.fetchSearchNext(2000);
				guard++;
			}
		}
	}

	async openFolderAsync(rowKey: JSrcFieldsTree): Promise<boolean> {
		try {
			if (this.folderSt(rowKey) !== EFolderState.closed) return false;
			this.setFolderSt(rowKey, EFolderState.opened);
			this.setChilrenTo(rowKey, [], this.defaultOpenState);
			if (await this.fetchSearchReplaceChildren(rowKey.srcUri)) {
				const offset = this.getOffset(rowKey);
				if (offset >= 0) this.grid.invalidateRows(offset, 1);
				return true;
			}
			return false;
		} catch (e) {
			POPUP.showNotifError("Accès au serveur impossible", this.grid);
			this.closeFolder(rowKey);
			throw e;
		}
	}

	closeFolder(rowKey: JSrcFieldsTree): boolean {
		if (this._nextSearch) return false; //Si search en cours, on bloque : sinon raceCond sur this._afterSrcUri
		if (super.closeFolder(rowKey)) {
			if (this._afterSrcUri && SRC.isSubUriOrEqual(rowKey.srcUri, this._afterSrcUri)) {
				//this._afterSrcUri dans le dossier clos, on se replace juste après ce dossier clos
				this._afterSrcUri = rowKey.srcUri;
				this._afterEndFolder = true;
			}
			return true;
		}
		return false;
	}

	/** Retourne null si aucune requete en cours de traitement. */
	get searching(): Promise<void> | null {return this._nextSearch}

	protected unlistenGridLimits() {
		if (this._grid) this._grid.reachedLimitsCb = null;
	}

	protected listenGridLimits() {
		if (this._afterSrcUri && this._grid) this._grid.reachedLimitsCb = (start) => {
			if (start) return;
			//console.log("reachedLimitsCb::::new _afterSrcUri, _afterEndFolder:::", this._afterSrcUri, this._afterEndFolder);
			this.fetchSearchNext(this.maxNextSearch);
		};
	}

	protected async _fetchSearchStart() {
		if (this.callbackSearching) this.callbackSearching.onSearching('searchInit');
		this.lastError = undefined;
		this.unlistenGridLimits();
		const datas = [] as JSrcFieldsTree[];
		try {
			const stamp = this.request.getStamp();
			const res = await WSP.fetchSearch(this.wsp, this._grid, this.request.getStartReq(this.maxStartSearch));
			if (!this._grid || !this.request.isSameStamp(stamp)) return; //raceCond
			this.resetAfterSrcUri();
			this.insertInTreeFromSearch(res.columns as ESrcField[], res.results, datas, false, res.results.length >= this.maxStartSearch);
			this.lastError = null;
			this.listenGridLimits();
			this.setDatas(datas);
		} catch (e) {
			this.lastError = e;
			this.setDatas([]);
			throw e;
		} finally {
			this._nextSearch = null;
			if (this.callbackSearching) this.callbackSearching.onSearching('searchEnd');
		}
	}

	protected async _fetchSearchNext(max: number): Promise<void> {
		try {
			if (this.callbackSearching) this.callbackSearching.onSearching('searchNext');
			this.lastError = undefined;
			this.unlistenGridLimits();
			const stamp = this.request.getStamp();
			const res = await WSP.fetchSearch(this.wsp, this._grid, this.request.getNextReq(this._afterEndFolder ? this._afterSrcUri + "//" : this._afterSrcUri, max));
			if (!this._grid || !this.request.isSameStamp(stamp)) return; //raceCond
			this.resetAfterSrcUri();
			this.insertInTreeFromSearch(res.columns as ESrcField[], res.results, this._datas, true, res.results.length >= max);
			this.lastError = null;
			this.listenGridLimits();
		} catch (e) {
			this.lastError = e;
			this.setDatas([]);
			throw e;
		} finally {
			this._nextSearch = null;
			if (this.callbackSearching) this.callbackSearching.onSearching('searchEnd');
		}
	}

	/** Retourne true si réalisé, pas abandonné pour raceCond*/
	protected async _fetchSearchReplaceChildren(folderUri: srcUri, max: number): Promise<boolean> {
		if (this.callbackSearching) this.callbackSearching.onSearching('searchInside');
		this.lastError = undefined;
		this.unlistenGridLimits();
		try {
			const stamp = this.request.getStamp();
			const res = await WSP.fetchSearch(this.wsp, this._grid, this.request.getReplaceFolderReq(folderUri, max));
			if (!this._grid || !this.request.isSameStamp(stamp)) return false; //raceCond
			const hasMore = res.results.length >= max;
			if (hasMore) this.resetAfterSrcUri();
			let last = this.insertInTreeFromSearch(res.columns as ESrcField[], res.results, this._datas, true, hasMore);
			if (hasMore && last) {
				//Le folder n'est pas complet, on doit éliminer toutes les datas à partir du nouveau this._afterSrcUri
				//console.log("HasMore in folder:::: this._afterSrcUri", this._afterSrcUri);
				while (last) {
					const lastP = this.parent(last);
					const ch = lastP ? this.ch(lastP) : this._datas;
					const idx = ch.lastIndexOf(last) + 1;
					const toDel = ch.length - idx;
					if (toDel > 0) this.updateDatasInTree(lastP, idx, toDel);
					last = lastP;
				}
			}
			this.lastError = null;
			this.listenGridLimits();
		} catch (e) {
			this.lastError = e;
			this.setDatas([]);
			throw e;
		} finally {
			this._nextSearch = null;
			if (this.callbackSearching) this.callbackSearching.onSearching('searchEnd');
		}
		return true;
	}

	protected resetAfterSrcUri() {
		this._afterSrcUri = null;
		this._afterEndFolder = false;
	}

	/**
	 * @return dernière entrée ajoutée ou undefined.
	 */
	protected insertInTreeFromSearch(keys: ESrcField[], values: any[][], rootDatas: JSrcFieldsTree[], updateGrid: boolean, hasMore: boolean): JSrcFieldsTree | undefined {
		if (!this._grid) return; // affichage clos
		try {
			this._grid.freezeRefresh();
			let data: JSrcFieldsTree;
			let lastSrcUri: string;
			for (let i = 0, s = values.length; i < s; i++) {
				const v = values[i];
				data = {};
				for (let k = 0; k < keys.length; k++) (data as Dict<any>)[keys[k]] = v[k];
				if (this.fieldsFilter && !this.fieldsFilter(data)) continue;
				lastSrcUri = data.srcUri;
				this.insertInTree(data, rootDatas, updateGrid);
			}
			if (hasMore) this._afterSrcUri = lastSrcUri;
			return data;
		} finally {
			this._grid.unfreezeRefresh();
		}
	}

	/**
	 * @param updateGrid false uniquement avant l'init du grid. setData(inRootDatas) sera appelé ensuite.
	 */
	protected insertInTree(toInsert: JSrcFields, inRootDatas: JSrcFieldsTree[], updateGrid: boolean) {
		let parent = null;
		let children = inRootDatas;
		let offsetInsert: number = updateGrid ? undefined : -1;
		let countInsert = 0;
		let prevSlash = this.uriRoot ? this.showRoot ? this.uriRoot.lastIndexOf('/') + 1 : this.uriRoot.length + 1 : 1;
		let uriToSplit = toInsert.srcUri;
		if (ITEM.isAirItem(uriToSplit) || ITEM.isExtItem(uriToSplit)) {
			//change l'uri pour fusionner l'id de l'item avec son code en un seul fragment.
			const i = uriToSplit.lastIndexOf('/');
			uriToSplit = uriToSplit.substring(0, i) + '_' + uriToSplit.substring(i + 1);
		}
		let nextSlash = uriToSplit.indexOf('/', prevSlash);
		//Insertion ou recherche de l'espace existant
		while (nextSlash > 0) {
			const srcUri = uriToSplit.substring(0, nextSlash);
			const index = this.findInChildren(children, srcUri);
			if (index >= 0) {
				parent = children[index];
				children = parent.ch;
				if (children == null) {
					//dossier fermé, on va le peupler, il devrait être ouvert après.
					children = parent.ch = [];
				}
			} else {
				const newFolder = {srcUri: srcUri, srcSt: ESrcSt.folder, ch: []} as JSrcFieldsTree;
				const index = this.findInsertPoint(children, newFolder);
				children.splice(index, 0, newFolder);
				if (updateGrid) {
					if (offsetInsert === undefined) offsetInsert = this.getOffsetInChildren(parent, children, index);
					this._initRowData(newFolder, parent, GridDataHolderJsonTree.defaultOpened);
					this.updateSubRowsShown(parent, 1);
					countInsert++;
				}
				parent = newFolder;
				children = newFolder.ch;
			}
			prevSlash = nextSlash + 1;
			nextSlash = uriToSplit.indexOf('/', prevSlash);
		}
		//si maj live on check si cette srcUri n'a pas été inséré entre temps (inutile sinon car 1er insert).
		if (updateGrid && this.findInChildren(children, toInsert.srcUri) >= 0) return;
		//Insertion de l'item
		const index = this.findInsertPoint(children, toInsert);
		children.splice(index, 0, toInsert);
		if (updateGrid) {
			if (offsetInsert === undefined) offsetInsert = this.getOffsetInChildren(parent, children, index);
			this._initRowData(toInsert, parent, GridDataHolderJsonTree.defaultClosed);
			this.updateSubRowsShown(parent, 1);
			countInsert++;
			if (this._grid && offsetInsert >= 0) this._grid.rowCountChanged(offsetInsert, countInsert);
		}
	}
}

/**
 * Source de données pour la vue avec filtre qui est "Live",
 * ie qui se met à jour dynamiquement en fonction des events wspUriChange.
 *
 * Cette contrainte "Live" réduit les possibilités de filtre au srcUri.
 */
export class SpaceTreeDataSearchLive extends SpaceTreeDataSearch {

	request: SpaceTreeReqUri;

	initSpTrDataLive(place?: WspsLivePlace): Promise<void> {
		this.initSpTrDataSearch(new SpaceTreeReqUri(this.wsp).initReq(this.wsp.getShortDescDef(), this.uriRoot), 75, 150);
		if (place) place.eventsMgr.on('wspUriChange', (msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') => {
			if (this.wsp.code !== msg.wspCd) return;
			const srcUri = !this.hideItemFolderContent && msg.shortDesc ? msg.shortDesc.srcUri : msg.srcUri; //msg.shortDesc.srcUri => resUri dans item folder.
			if (ITEM.isSpecialUri(srcUri)) return;
			const node = this.findRowKeyBySrcUri(srcUri);
			if (node) {
				if (this.shouldHideEntry(msg, node)) {
					this.deleteRowKey(node);
				} else if (msg.type === EWspChangesEvts.u && ITEM.getSrcUriType(srcUri) === "space") {
					//msg.type===u signifie en général un changement du contenu de l'espace issu d'une fusion de 2 dossiers.
					//=> On requête sur tout l'espace et on le remplace.
					this.fetchSearchReplaceChildren(srcUri);
				} else {
					this.refreshRowKey(node);
				}
			} else if (ITEM.getSrcUriType(srcUri) === "space") {
				if (msg.type === EWspChangesEvts.u) {
					//msg.type===u signifie en général un changement du contenu de l'espace issu d'une fusion de 2 dossiers.
					if (!this._afterSrcUri || this.wsp.srcUriItemsSortFn(srcUri, this._afterSrcUri) <= 0) {
						//cet espace est dans la zone déjà construite des résultats
						//=> On requête sur tout l'espace et on l'insère.
						this.fetchSearchReplaceChildren(srcUri);
					}
				}
			} else if (this.request.isSrcUriMustBeIncluded(srcUri) && !this.shouldHideEntry(msg)) {
				this.insertSrcUriInTree(srcUri, msg.shortDesc);
			}
		});
		if (place) place.eventsMgr.on("onConnectionRenewed", () => {
			this.fetchSearchStart();
		});
		return null;
	}

	/**
	 * Charge la liste jusqu'à l'uri soit présente dans la liste.
	 * Retourne false si srcUri ne matche pas les critères de sélection.
	 */
	async fetchSrcUri(srcUri: srcUri): Promise<boolean> {
		if (!this.request.isSrcUriMustBeIncluded(srcUri)) return false;
		let guard = 0;
		while (this._afterSrcUri && this.wsp.srcUriItemsSortFn(srcUri, this._afterSrcUri) > 0 && guard < 100) {
			await this.fetchSearchNext(2000);
			guard++;
		}
		return true;
	}

	async fetchSrcUris(srcUris: srcUri[]): Promise<void> {
		let guard = 0;
		for (const srcUri of srcUris) {
			if (!this.request.isSrcUriMustBeIncluded(srcUri)) continue;
			while (this._afterSrcUri && this.wsp.srcUriItemsSortFn(srcUri, this._afterSrcUri) > 0 && guard < 100) {
				await this.fetchSearchNext(2000);
				guard++;
			}
		}
	}

	protected async insertSrcUriInTree(srcUri: srcUri, shortDesc: JSrcFields) {
		const fields = shortDesc || await this.wsp.fetchShortDesc(srcUri, this._grid);
		if (this.fieldsFilter && !this.fieldsFilter(fields)) return;
		if (this._nextSearch) await this._nextSearch;
		if (this.request.isSrcUriMustBeIncluded(srcUri)) this.insertInTree(Object.assign(Object.create(null), fields), this._datas, true);
	}
}

/**
 * Abstraction des critères d'une recherche pour afficher un SpaceTree.
 */
export interface ISpaceTreeReq {

	/** Retourne la requête initiale (xml sérialisé) */
	getStartReq(max: number): string

	/** Retourne la requête de pagination suivante. */
	getNextReq(afterSrcUri: srcUri, max: number): string

	/**
	 * Construit la requête pour recharger un espace/dossier entier.
	 */
	getReplaceFolderReq(folderUri: srcUri, max: number): string

	/**
	 * Avec isSameStamp() permet d'évaluer si les critères
	 * ont été modifiés au cours d'un traitement async.
	 */
	getStamp(): any

	isSameStamp(stamp: any): boolean
}

/**
 * Critères de recherche progressive pour un SpaceTreeDataSearchLive, c.a.d. qui doit se mettre à jour dynamiquement.
 */
export interface ISpaceTreeReqLive<P> extends ISpaceTreeReq {

	/**
	 * Met à jour les paramètres de la requete et retourne true s'ils ont effectivement été modifiés
	 */
	updateParams(params?: P): boolean

	/**
	 * Suite à evt de modif d'une srcUri évalue si cette srcUri
	 * devrait être insérée dans l'arbre.
	 */
	isSrcUriMustBeIncluded(srcUri: srcUri): boolean;
}

/** Requête limitée à un filtre sur le srcUri. */
export class SpaceTreeReqUri implements ISpaceTreeReqLive<{ uriFilter: string }> {

	uriRoot: string;

	protected _textToSearch: string;

	protected _lastRegexpFetch: RegExp;


	protected _req: Element;
	protected _itemsExp: Element;
	protected _srcUriExp: Element;
	protected _selectReq: Element;

	constructor(protected wsp: Wsp) {}

	static buildSrcUriPattern(text: string): RegExp {
		const slash = text.indexOf('/');
		if (slash >= 1) {
			//match un espace avant
			const space = text.substring(0, slash);
			text = text.substring(slash + 1);
			return new RegExp(`.*${LANG.escape4RegexpFuzzy(space)}.*/.*${LANG.escape4RegexpFuzzy(text)}[^\/]*`, 'i');
		}
		return new RegExp(`.*${LANG.escape4RegexpFuzzy(text)}[^\/]*`, 'i');
	}

	initReq(fields: string | ESrcField[], uriRoot: string): this {
		this.uriRoot = uriRoot;
		const mainCrit = new SearchItems().excludeTildeRoot().includeDrfErased(this.wsp.wspDefProps.drfRefWsp != null);
		this._req = JSX.asXml(() => <request>
			<select>{Array.isArray(fields) ? fields.map((f) => JSX.asXml(() => <column dataKey={f}/>)) : <columns dataKeys={fields}/>}</select>
			<where xmlns:sc="http://www.utc.fr/ics/scenari/v3/core">
				{mainCrit.toDom()}
				<exp type="RegexpUri"/>
			</where>
		</request>);
		//console.log("req::::::", DOM.ser(this._req));
		this._itemsExp = this._req.querySelector('exp[type=Items]');
		if (uriRoot) this._itemsExp.setAttribute('beforeEndFolder', uriRoot + "/");
		this._srcUriExp = this._itemsExp.nextElementSibling;
		this._selectReq = this._req.firstElementChild;
		return this;
	}

	updateParams(params?: { uriFilter: string }): boolean {
		const newTxt = params.uriFilter.toLowerCase();
		if (this._textToSearch === newTxt) return false;
		this._textToSearch = newTxt;
		this._lastRegexpFetch = SpaceTreeReqUri.buildSrcUriPattern(newTxt);
		return true;
	}

	getStartReq(max: number): string {
		this._srcUriExp.setAttribute("regexp", `(?i)${this._lastRegexpFetch.source}`);
		this._selectReq.setAttribute("max", max.toString());
		if (this.uriRoot) this._itemsExp.setAttribute('afterUri', this.uriRoot + "/");
		else this._itemsExp.removeAttribute('afterUri');
		this._itemsExp.removeAttribute('beforeEndFolder');
		return DOM.ser(this._req);
	}

	getNextReq(afterSrcUri: srcUri, max: number): string {
		this._selectReq.setAttribute("max", max.toString());
		this._itemsExp.setAttribute('afterUri', afterSrcUri);
		this._itemsExp.removeAttribute('beforeEndFolder');
		return DOM.ser(this._req);
	}

	getReplaceFolderReq(folderUri: srcUri, max: number): string {
		this._selectReq.setAttribute("max", max.toString());
		const path = folderUri + '/';
		this._itemsExp.setAttribute('afterUri', path);
		this._itemsExp.setAttribute('beforeEndFolder', path);
		return DOM.ser(this._req);
	}

	getStamp(): any {return this._lastRegexpFetch}

	isSameStamp(stamp: any): boolean { return stamp === this._lastRegexpFetch}

	isSrcUriMustBeIncluded(srcUri: srcUri): boolean {
		if (this.uriRoot && !SRC.isSubUri(this.uriRoot, srcUri)) return false;
		return this._lastRegexpFetch ? this._lastRegexpFetch.test(srcUri) : false;
	}
}


// function transformArrayToObj<D>(keys: (keyof D)[], values: any[][]): D[] {
// 	for (let i = 0; i < values.length; i++) {
// 		let v = values[i];
// 		let d = {} as D;
// 		for (let k = 0; k < keys.length; k++) d[keys[k]] = v[k];
// 		values[i] = d as any;
// 	}
// 	return values as any as D[];
// }

/** Gestion du drop dans l'arbre. */
const rowDropMgr = {
	onDragOverRow(ev: DragEvent, grid: IGrid, row: GridDataRowJson<JSrcFields>, line: HTMLElement): EGridDropPos | EGridDropPos {
		ev.dataTransfer.dropEffect = 'none';
		if (row && row.rowDatas.srcSt !== ESrcSt.folder) return EGridDropPos.none;
		const spaceTree = DOMSH.findHost<SpaceTree>(grid);
		const srcFields = row ? row.rowDatas : spaceTree.datas.rootSrcFields;
		if (!srcFields) return EGridDropPos.none;
		const srcUriType = ITEM.getSrcUriType(srcFields.srcUri);
		if (spaceTree.datas.hideItemFolderContent) {
			//dropable que sur les espaces.
			if (srcUriType !== "space") return EGridDropPos.none;
		} else {
			//dropables, les items de famille "folder" et les res (de srcSt folder).
			if (srcUriType === "item" && !spaceTree.wsp.wspMetaUi.getItemType(srcFields.itModel).isFamily(EItemTypeFamily.folder, EItemTypeFamily.undef, EItemTypeFamily.other)) {
				return EGridDropPos.none;
			}
		}
		if (!spaceTree.reg.hasPermission("action.src#importTo", srcFields.srcRoles, undefined, srcFields.srcRi)) {
			return EGridDropPos.none;
		}
		const transfer = ITEM.getShortDescsTransferFromDragSession(spaceTree.reg, ev.dataTransfer, {targetSrcType: srcUriType === "space" ? "itemOrSpace" : "res", canCreate: true}, grid);
		const ctx = {
			shortDescs: [srcFields] as JSrcFields[],
			reg: spaceTree.reg,
			emitter: grid,
			me: spaceTree
		} as ISpaceTreeCtx;
		const act = SpaceTreePaste.getProxiedAction(ctx, transfer, !ACTION.isCopyOverMove(ev));
		if (!act) return EGridDropPos.none;
		ctx.shortDescs = transfer.shortDescs;
		if (act && act.isAvailable(ctx)) {
			ev.dataTransfer.dropEffect = (act as IMoveAction).isMove ? 'move' : 'copy';
			return EGridDropPos.over;
		}
		return EGridDropPos.none;
	},

	async dropOnRow(ev: DragEvent, grid: IGrid, row: GridDataRowJson<JSrcFields>, line: HTMLElement, pos: EGridDropPos) {
		const spaceTree = DOMSH.findHost<SpaceTree>(grid);
		const srcFields = row ? row.rowDatas : spaceTree.datas.rootSrcFields;
		const transfer = ITEM.getShortDescsTransferFromDragSession(spaceTree.reg, ev.dataTransfer, {targetSrcType: ITEM.getSrcUriType(srcFields.srcUri) === "space" ? "itemOrSpace" : "res", canCreate: true}, grid);
		const ctx = {
			shortDescs: [srcFields] as JSrcFields[],
			reg: spaceTree.reg,
			emitter: grid,
			me: spaceTree
		} as ISpaceTreeCtx;
		const act = SpaceTreePaste.getProxiedAction(ctx, transfer, !ACTION.isCopyOverMove(ev) /*ev.dataTransfer.dropEffect==='move' BUG chrome 71 ?*/);
		if (act) {
			if (transfer.extractDatasNow) await transfer.extractDatasNow(ev.dataTransfer);
			ctx.shortDescs = transfer.shortDescs;
			act.executeIfAvailable(ctx, ev);
		} else {
			POPUP.showNotifInfo(`Format non autorisé`, line);
		}
	},


} as IGridRowDropMgr;


//FIXME supprimer ces actions spécialisées en utilisant InfoFocusItem et InfoSelectUris dans les implémentations génériques ?

/** Force un reload au double-click sur l'item courant. Effectue un toggle sur un espace. */
export class SpaceTreeFocusItem extends FocusItemOrSpace {

	getLabel(ctx: SpaceTree): string {
		return ctx.currentDatas.getRowKeyFolderState(ctx.shortDescs[0]) === EFolderState.opened ? "Refermer cet espace" : super.getLabel(ctx);
	}

	async execute(ctx: SpaceTree, ev?: Event) {
		if (ITEM.getSrcUriType(ctx.shortDescs[0]?.srcUri) === "space") {
			ctx.currentDatas.toggleFolder(ctx.shortDescs[0]);
		} else if (ctx._currentSrcUri === ctx.shortDescs[0].srcUri) {
			const wsp = ctx.reg.env.wsp;
			return wsp.wspServer.wspsLive.reloadHouse(WSP.buildWspRef(wsp.code, ctx.shortDescs[0]));
		} else {
			super.execute(ctx, ev);
		}
	}
}

/**
 * Création d'un item (spécifique à la view SpaceTree).
 */
export class SpaceTreeCreateItem extends CreateItem<SpaceTree> {

	async execute(ctx: SpaceTree, ev?: Event): Promise<JSrcFields> {
		const sd = await super.execute(ctx, ev);
		ctx.emitter.focus();
		if (sd) {
			if (await ctx.selectSrcUri(sd.srcUri)) {
				ctx.grid.defaultAction?.executeIfAvailable(ctx);
			} else {
				//Echec à l'affichage dans le spaceTree, si c'est un airItem par exemple.
				//Problème : court-circuite la config de SpaceTree.grid.defaultAction
				//Configurer ce comportement ? ou appeler ctx.grid.defaultAction avec un Ctx hacké ?
				ctx.reg.env.infoBroker?.dispatchInfo(new InfoFocusItem(SRC.srcRef(sd), sd), ctx.reg.env.uiRoot);
			}
		}
		return sd;
	}
}


/**
 * Création d'un espace (spécifique à la view SpaceTree).
 */
export class SpaceTreeCreateSpace extends CreateSpace<SpaceTree> {

	async execute(ctx: SpaceTree, ev?: Event): Promise<JSrcFields> {
		const shortDesc = await super.execute(ctx, ev);
		ctx.emitter.focus();
		if (shortDesc && await ctx.selectSrcUri(shortDesc.srcUri) && ctx.grid.defaultAction) ctx.grid.defaultAction.executeIfAvailable(ctx);
		return shortDesc;
	}

	protected getParentSpace(ctx: SpaceTree, ev?: Event): string {
		return super.getParentSpace(ctx, ev) || ctx.uriRoot || "";
	}
}

export class SpaceTreeRename extends RenameSrc<ISpaceTreeCtx> {

	async execute(ctx: ISpaceTreeCtx, ev?: Event): Promise<srcUri[]> {
		const newUris = await super.execute(ctx, ev);
		ctx.emitter.focus();
		if (newUris) {
			//Pas de possibilité actuellement coté java d'exclure notre clId de la remontée des wspChanges events sur un rename.
			//if (await ctx.selectSrcUri(newUri) && ctx.grid.defaultAction) ctx.grid.defaultAction.executeIfAvailable(ctx);
			//Donc pas d'autre solution qu'un timeout pour re-sélectionner la cible du renommage.
			setTimeout(() => {
				ctx.me.selectSrcUris(newUris);
				openUri(newUris, ctx);
			}, 300);
		}
		return newUris;
	}
}

export class SpaceTreeDuplicate extends DuplicateSrc<ISpaceTreeCtx> {

	/** Appelable dans le contexte d'un SpaceTree ou d'un SpaceTree wrappé (cf SpaceTreePaste). */
	async execute(ctx: ISpaceTreeCtx, ev?: Event): Promise<srcUri[]> {
		const newUris = await super.execute(ctx, ev);
		if (newUris) {
			//Pas de possibilité actuellement coté java d'exclure notre clId de la remontée des wspChanges events sur un rename.
			//if (await ctx.selectSrcUri(newUri) && ctx.grid.defaultAction) ctx.grid.defaultAction.executeIfAvailable(ctx);
			//Donc pas d'autre solution qu'un timeout pour re-sélectionner la cible du renommage.
			setTimeout(() => {
				ctx.emitter.focus();
				ctx.me.selectSrcUris(newUris);
				openUri(newUris, ctx);
			}, 300);
		}
		return newUris;
	}
}

export class SpaceTreeCopyTo extends CopyToSrc<ISpaceTreeCtx> {

	/** Appelable dans le contexte d'un SpaceTree ou d'un SpaceTree wrappé (cf SpaceTreePaste). */
	async execute(ctx: ISpaceTreeCtx, ev?: Event): Promise<srcUri[]> {
		const newUris = await super.execute(ctx, ev);
		ctx.emitter.focus();
		if (newUris) {
			//Pas de possibilité actuellement coté java d'exclure notre clId de la remontée des wspChanges events sur un moveTo.
			//await ctx.selectSrcUris(newUris);
			//Donc pas d'autre solution qu'un timeout pour re-sélectionner la cible du renommage.
			setTimeout(() => {
				ctx.me.selectSrcUris(newUris);
				openUri(newUris, ctx);
			}, 300);
		}
		return newUris;
	}
}

export class SpaceTreeImportTo extends ImportItemsToSrc<ISpaceTreeCtx> {

	/** Appelable dans le contexte d'un SpaceTree ou d'un SpaceTree wrappé (cf SpaceTreePaste). */
	async execute(ctx: ISpaceTreeCtx, ev?: Event): Promise<JSrcFields[]> {
		const newSrcs = await super.execute(ctx, ev);
		ctx.emitter.focus();
		if (newSrcs) {
			await ctx.me.selectSrcUris(newSrcs.map(s => s.srcUri));
			//Pas de possibilité actuellement coté java d'exclure notre clId de la remontée des wspChanges events sur un moveTo.
			//Donc pas d'autre solution qu'un timeout pour re-sélectionner la cible du renommage.
			// setTimeout(() => {
			// 	ctx.me.selectSrcUris(newSrcs);
			// }, 300);
		}
		return newSrcs;
	}
}


export class SpaceTreeMoveTo extends MoveToSrc<ISpaceTreeCtx> {

	/** Appelable dans le contexte d'un SpaceTree ou d'un SpaceTree wrappé (cf SpaceTreePaste). */
	async execute(ctx: ISpaceTreeCtx, ev?: Event): Promise<srcUri[]> {
		const newUris = await super.execute(ctx, ev);
		ctx.emitter.focus();
		if (newUris) {
			//Pas de possibilité actuellement coté java d'exclure notre clId de la remontée des wspChanges events sur un moveTo.
			//if (await ctx.selectSrcUri(newUri) && ctx.grid.defaultAction) ctx.grid.defaultAction.executeIfAvailable(ctx);
			//Donc pas d'autre solution qu'un timeout pour re-sélectionner la cible du renommage.
			setTimeout(() => {
				ctx.me.selectSrcUris(newUris);
				openUri(newUris, ctx);
			}, 300);
		}
		return newUris;
	}
}

/** Cas partiluier si un seul item a été dupliqué ou déplacé on l'ouvre. */
function openUri(newUris: srcUri[], ctx: ISpaceTreeCtx) {
	if (newUris.length === 1 && ctx.infoBroker) {
		const uri = newUris[0];
		if (ITEM.getSrcUriType(uri) === "item") ctx.infoBroker.dispatchInfo(new InfoFocusItem(uri), ctx.me);
	}
}

interface ISpaceTreePaste {
	/** false pa défaut */
	isCtxInitialized?: boolean
}

/**
 * Coller dans la view SpaceTree.
 * ATTENTION: Cette action n'est pas utilisable en toolbar : l'état aysnchrone du clipboard n'est pas rafraichit.
 * Utilisé en menu contextuel et racourci clavier.
 *
 * Cette implémentation est un proxy vers l'action réelle qui sera executée en asynchrone.
 * Elle sert aussi de controller central pour d'autres actions exploitant le clipboard (cf SpaceTreePasteMove).
 */
export class SpaceTreePaste extends SrcAction<ISpaceTreeCtx> {

	/**
	 * A ce stade ctx.shortDescs correspond à la cible du paste.
	 * L'action retournée attend un ctx où ctx.shortDescs = transfer.shortDescs
	 */
	static getProxiedAction(ctx: ISpaceTreeCtx, transfer: IShortDescsTransfer | ClipboardItem[], preferMove?: boolean): Action<ISpaceTreeCtx> | null {
		if (!transfer) return null;
		if (Array.isArray(transfer)) {
			//Import d'un stream dans le src courant
			const bestType = WriteMainStreamToSrc.findBestType(ctx, transfer);
			if (bestType) return new WriteMainStreamToSrc(transfer[0].getType(bestType));
			//Tentative de création d'item si on est sur un espace
			const spaceUri = ctx.shortDescs[0]?.srcUri || "";
			const clipboardItem = transfer[0];
			if (clipboardItem && ITEM.getSrcUriType(spaceUri) === "space") {
				const svc = ctx.reg.getSvc<IImportInWspSvc>("wspImportInWsp");
				if (svc) {
					const types = clipboardItem.types;
					let bestCtType: string
					let bestScore = 0
					for (const itemType of ctx.reg.env.wsp.wspMetaUi.getItemTypes()) {
						for (const t of types) {
							const score = itemType.matchType(t)
							if (score > bestScore) {
								bestScore = score;
								bestCtType = t;
							}
						}
					}
					if (bestCtType) {
						const promiseBody = clipboardItem.getType(bestCtType);
						const spaceTree = ctx.me;
						const uiCtx = ctx.emitter;
						return new SrcAction<ISpaceTreeCtx>("paste").setLabel("Coller (importer)...").requireSrcEnabledPerm("action.src#importTo").setExecute(async (ctx: ISpaceTreeCtx) => {
							const newSrc = await svc.importSrc(await promiseBody, {targetSrcType: "item", defaultUriParent: spaceUri}, ctx.reg, uiCtx);
							if (newSrc) {
								if (await spaceTree.selectSrcUri(newSrc.srcUri)) {
									spaceTree.grid.defaultAction?.executeIfAvailable(ctx);
								} else {
									//Echec à l'affichage dans le spaceTree, si c'est un airItem par exemple.
									//Problème : court-circuite la config de SpaceTree.grid.defaultAction
									//Configurer ce comportement ? ou appeler ctx.grid.defaultAction avec un Ctx hacké ?
									ctx.reg.env.infoBroker?.dispatchInfo(new InfoFocusItem(SRC.srcRef(newSrc), newSrc), ctx.reg.env.uiRoot);
								}
							}
							return newSrc;
						})
					}
				}
			}
			return null;
		}
		if (!transfer.isImport && !(transfer.shortDescs?.length > 0)) {
			if (transfer.server !== ctx.reg.env.universe.config.universeUrl.url) {
				//serveur différent (todo wspImportInterRepos)
				return new Action<ISpaceTreeCtx>("paste").setLabel("Coller (d'un autre entrepôt)").setDescription("Il est impossible d'exploiter des items d'un autre entrepôt.").setEnabled(false);
			}
			return null; //pas de source trouvée dans le clipboard
		}
		if (transfer.shortDescs.findIndex((src) => TASK.isTask(src)) >= 0) {
			//Interdit de coller des tâches.
			return null;
		}
		const targetUri = ctx.me.extractFolderUri(ctx.shortDescs[0]);
		const targetSrcFields = ctx.me.extractFolderSrcFields(ctx.shortDescs[0]);
		const targetWsp = ctx.reg.env.wsp;
		if (transfer.wspCd && transfer.wspCd !== targetWsp.code) {
			//transfer est issu d'un wsp (pas file, stream...) qui n'est pas le notre.
			return new Action<ISpaceTreeCtx>("paste").setLabel("Coller (d'un autre atelier)").setExecute(async (ctx: ISpaceTreeCtx) => {
				const svcImportInterWsp = ctx.reg.getSvc<IImportInterWspSvc>("wspImportInterWsp");
				if (!svcImportInterWsp) {
					POPUP.showNotifInfo("Coller des contenus issus d'un autre atelier est impossible.", ctx.emitter);
				} else {
					const newSrcUris: srcUri[] = [];
					const opts = {
						refExtItemAcceptable: false,
						defaultUriParent: targetUri,
						uriParentFrozen: true,
						allowReplace: true,
						repeatedImports: transfer.shortDescs.length > 0 ? {} : undefined
					} as OImportInterWspOptions
					for (const sd of transfer.shortDescs) {
						const result = await svcImportInterWsp.importSrc(transfer.wsp, sd, opts, ctx.reg, ctx.emitter)
						if (!result) break;
						newSrcUris.push(result.srcUri);
					}
					ctx.emitter.focus();
					if (newSrcUris.length > 0) await ctx.me.selectSrcUris(newSrcUris);
				}
			});
		}
		if (transfer.isImport) {
			return new SpaceTreeImportTo(transfer).setTarget(targetUri, 'hide');
		}
		if (transfer.shortDescs.findIndex((src) => SRC.extractUriParent(src.srcUri) !== targetUri) < 0) {
			//Tous les éléments du clipboard sont fils de la cible => on duplique
			return new SpaceTreeDuplicate('paste').setLabel("Coller (dupliquer)...");
		}
		if (transfer.shortDescs.length === 1 && transfer.shortDescs[0].srcUri === targetUri) {
			//Copier/coller sur le folder lui-même, on duplique
			return new SpaceTreeDuplicate('paste').setLabel("Coller (dupliquer)...");
		}
		if (CopyToSrc.makeTargetFilter(transfer.shortDescs)(targetUri)) {
			//Recopie dans cette cible possible.
			if (preferMove) {
				return new SpaceTreeMoveTo('paste').setTarget(targetSrcFields || targetUri, "hide").setLabel("Coller (déplacer)");
			} else {
				return new SpaceTreeCopyTo('paste').setTarget(targetSrcFields || targetUri, "hide").setLabel("Coller (recopier)");
			}
		}
		return null;
	}

	protected pending: Promise<IShortDescsTransfer | false | null | ClipboardItem[]>;
	protected pendingBtns: ActionBtn<ISpaceTreeCtx>[];

	constructor() {
		super('paste');
		this._label = "Coller";
		this._group = "clipboard";
	}

	//A l'init, toujours inactif le temps d'analyser le clipboard en async
	isEnabled(ctx: ISpaceTreeCtx & ISpaceTreePaste): boolean {return ctx.isCtxInitialized ? super.isEnabled(ctx) : false}

	/** On bloque dans les barre d'outils. */
	buildCustomButton(ctx: ISpaceTreeCtx, uiContext: EButtonUiContext, parent?: Element): Element | null | undefined {return uiContext === 'bar' ? null : undefined}

	async initButtonNode(buttonNode: Element, ctx: SpaceTree) {
		if (buttonNode instanceof ActionBtn) {
			if (!this.pendingBtns) this.pendingBtns = [buttonNode];
			else this.pendingBtns.push(buttonNode);
			if (!this.pending) {
				this.pending = ITEM.getShortDescsTransferFromClipboard(ctx.reg.env.wsp, ctx.emitter);
				try {
					const srcs = await this.pending;
					if (srcs && this.pendingBtns) for (const btn of this.pendingBtns) {
						(btn.action as SpaceTreePaste | SpaceTreePasteMove).updateButton(btn, srcs);
					}
				} finally {
					this.pendingBtns = null;
					this.pending = null;
				}
			}
		}
	}

	updateButton(btn: ActionBtn<ISpaceTreeCtx>, transfer: IShortDescsTransfer | ClipboardItem[]) {
		const action = SpaceTreePaste.getProxiedAction(btn.actionContext, transfer);
		if (action) {
			const newCtx: ISpaceTreeCtx & ISpaceTreePaste = Object.create(btn.actionContext);
			if ("shortDescs" in transfer) newCtx.shortDescs = transfer.shortDescs;
			btn.resetAction(action, newCtx);
		}
	}

	/** Apppelé par le racourci clavier uniqument. */
	async execute(ctx: ISpaceTreeCtx, ev?: Event): Promise<void> {
		const transfer = await ITEM.getShortDescsTransferFromClipboard(ctx.reg.env.wsp, ctx.emitter);
		if (!transfer) return;
		const action = SpaceTreePaste.getProxiedAction(ctx, transfer);
		if (action == null) return;
		const newCtx = Object.create(ctx);
		Object.defineProperty(newCtx, "me", {value: ctx.me}); //on n'a pas le droit de pointer les props d'un HTMLElement (nodeType, ...) dans une chaine de prototype
		if ("shortDescs" in transfer) newCtx.shortDescs = transfer.shortDescs;
		action.executeIfAvailable(newCtx, ev);
	}

	//on court-circuite la rééval 'isEnabled', pas calculable en synchrone.
	isAvailable(ctx: IShortDescCtx): boolean {return true}

}

/**
 * 2ème menu Coller dans la view SpaceTree, dédié au déplacement de sources (remplace le Cut).
 * ATTENTION : Ne peut être utilisé en racourci clavier, ni en toolbar.
 * Doit n'être utilisé qu'en menu contextuel (interrogation asynchrone du clipboard à l'affichage)
 */
export class SpaceTreePasteMove extends SpaceTreeMoveTo {
	/** On pointe l'action prinicpale Coller pour mutualiser l'appel au clipboard. */
	constructor(public ctrl: SpaceTreePaste) {
		super('pasteMove');
		this._label = "Coller (déplacer)";
		this._group = "clipboard";
		this.atLeastOne = false;
	}

	/** A l'init, toujours inactif le temps d'analyser le clipboard en async. */
	isEnabled(ctx: ISpaceTreeCtx & ISpaceTreePaste): boolean {return ctx.isCtxInitialized ? super.isEnabled(ctx) : false}

	/** On bloque dans les barre d'outils. */
	buildCustomButton(ctx: ISpaceTreeCtx, uiContext: EButtonUiContext, parent?: Element): Element | null | undefined {return uiContext === 'bar' ? null : undefined}

	/** on redirige sur le controler unique. */
	initButtonNode(buttonNode: Element, ctx: SpaceTree) {
		this.ctrl.initButtonNode(buttonNode, ctx);
	}

	/** Active le bouton si un déplacement de sources est possible. */
	updateButton(btn: ActionBtn<ISpaceTreeCtx>, transfer: IShortDescsTransfer | ClipboardItem[]) {
		if (!transfer || Array.isArray(transfer)) return;
		if (!transfer.shortDescs || transfer.shortDescs.length === 0 || transfer.isImport) return; //pas de source trouvée dans le clipboard
		if (transfer.shortDescs.findIndex((src) => TASK.isTask(src)) >= 0) return;

		const sourceCtx: ISpaceTreeCtx & ISpaceTreePaste = Object.create(btn.actionContext);
		if ("shortDescs" in transfer) sourceCtx.shortDescs = transfer.shortDescs;
		sourceCtx.isCtxInitialized = true;
		btn.resetAction(btn.action as Action<ISpaceTreeCtx>, sourceCtx);
		if (!this.isEnabled(sourceCtx)) return;

		const targetCtx = btn.actionContext;
		if (targetCtx.reg.env.wsp.code !== transfer.wspCd) return; //move inter-atelier impossible.
		const targetUri = targetCtx.me.extractFolderUri(targetCtx.shortDescs[0]);
		if (!MoveToSrc.makeTargetFilter(transfer.shortDescs)(targetUri)) return; //cible du déplacement incompatible avec les sources du clipboard
		btn.disabled = false; //on active le bouton
	}

	async execute(ctx: ISpaceTreeCtx, ev?: Event): Promise<srcUri[] | null> {
		const targetUri = ctx.me.extractFolderUri(ctx.shortDescs[0]);
		const transfer = await ITEM.getShortDescsTransferFromClipboard(ctx.reg.env.wsp, ctx.emitter);
		if (!transfer || Array.isArray(transfer)) return null;
		if (!transfer.shortDescs || transfer.shortDescs.length === 0) return null;
		if (transfer.shortDescs.findIndex((src) => TASK.isTask(src)) >= 0) return null;
		//On spécifie la target.
		this.setTarget(targetUri, "hide");
		const newCtx = Object.create(ctx);
		newCtx.shortDescs = transfer.shortDescs;
		return super.execute(newCtx, ev);
	}

	//on court-circuite la rééval 'isEnabled', pas calculable en synchrone.
	isAvailable(ctx: IShortDescCtx): boolean {return true}
}


export class SpaceTreeCollapseAllAction extends WspAction<ISpaceTreeCtx> {
	constructor(id?: string) {
		super(id || "collapseAll");
		this._label = "Tout refermer";
		this._group = "open";
	}

	isVisible(ctx: ISpaceTreeCtx): boolean {
		if (ctx.shortDescs.length == 1) return false;
		const datas = ctx.me.currentDatas;
		if ((ctx.shortDescs.length === 0 ? datas.getDatas() : ctx.shortDescs).findIndex((sd) => datas.getRowKeyFolderState(sd) === EFolderState.opened) < 0) return false;
		return super.isVisible(ctx);
	}

	execute(ctx: ISpaceTreeCtx, ev?: Event): any | "noPreventDefault" | "noStopPropag" | "noStopPropag-noPreventDefault" {
		ctx.me.currentDatas.closeAllFolders(ctx.shortDescs.length > 0 ? ctx.shortDescs : undefined, false);
	}
}

export class CellBuilderSpaceTree extends CellBuilderSrcIconCodeTitle {

	constructor(public spaceTree: SpaceTree) {
		super(spaceTree.reg, spaceTree.wsp.wspMetaUi, false, spaceTree.wsp.srcUriItemsSortFn);
	}

	redrawCell(row: GridDataRowJson<JSrcFields>, root: HTMLElement): void {
		(root as JSrcFieldsPointer)._srcFields = row.rowDatas;
		let span = root.firstElementChild as HTMLElement;
		if (!span) span = this._buildContent(row, root);
		this.redrawIcon(row, span);
		span = span.nextElementSibling as HTMLElement;
		DOM.setTextContent(span, this._getValue(row));
		span = span.nextElementSibling as HTMLElement;
		DOM.setTextContent(span, this.spaceTree.showTitleLive ? this._getTitle(row) : null);
	}
}

const DEBUG = false; //false
